/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from "react";
import {ArrowPathIcon, CheckIcon} from "@heroicons/react/24/outline";
import clsx from "clsx";
import ModalScrollableClose from "components/ModalScrollableClose";
import CardComponent from "pages/Front/Booking/components/CardComponent";
import {useDispatch} from "useDispatch";
import {useTranslation} from "react-i18next";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import BookingInputTimePicker
    from "pages/Front/Booking/Seller/BookingSellerReservationDetail/components/BookingInputTimePicker";
import UiButton, {UiButtonSize, UiButtonStyle} from "pages/Front/components/Ui/components/UiButton";
import {SellerReservationRebookingRequest} from "types/entities/SellerReservations";
import {consoleLog} from "utils/utility";
import SellerAction from "store/seller/SellerAction";
import {toast} from "react-toastify";
import {useHistory} from "react-router";

interface SellerRebookingFormInputs {
    date: string;
    time: string;
    reservation_id: number;
}

interface BookingSellerRebookingModalProps {
    reservation_id: number;
}

const BookingSellerRebookingModal = (props: BookingSellerRebookingModalProps) => {
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const history = useHistory();
    const defaultValues = {
        date: "",
        time: "",
        reservation_id: props.reservation_id,
    };
    const formSchema = yup.object().shape({
        date: yup.string().required(t("Date is required field.")),
        time: yup.string().required(t("Time is required field.")),
        reservation_id: yup.number().required(),
    });
    const {
        register,
        handleSubmit,
        setValue,
        formState: {errors},
    } = useForm<SellerRebookingFormInputs>({
        defaultValues: defaultValues,
        resolver: yupResolver(formSchema),
    });

    const setValueTimeHandler = (name: string, value: string) => {
        if (name == "time") {

            const time = extractTimeFromDate(value);
            //@ts-ignore
            setValue("time", time as string);
        }

    };

    const extractTimeFromDate = (date: string): string => {
        const dateObj = new Date(date);
        const hours = dateObj.getHours().toString().padStart(2, "0");
        const minutes = dateObj.getMinutes().toString().padStart(2, "0");
        return `${hours}:${minutes}`;
    };
    const onErrorHandler = (data: any) => {
        consoleLog("onErrorHandler", data);
    };

    const onSubmitHandler = (data: SellerRebookingFormInputs) => {
        consoleLog("onSubmitHandler", data);

        const dataForRequest: SellerReservationRebookingRequest = {...data};
        dispatch(SellerAction.fetchSetReservationRebooking(dataForRequest))
            .then(
                (onResolve) => {
                    dispatch(SellerAction.setReservation(onResolve.reservation));
                    toast.success(t("Offer has been successfully rebooked."));
                },
                (onReject) => {
                    toast.error(t("Error rebooking"));
                },
            ).catch(err => {
                if (err?.status && err?.status === 401) {
                    history.replace("/booking/seller/login");
                }
            });
    };

    return (
        <CardComponent className={"BookingSellerRebookingModal sm:w-[500px] w-full !p-0 border border-gray-200"}>
            <div
                className={"flex flex-row justify-between w-full px-3 py-3 font-bold border-b-2 border-gray-200 bg-gray-100/20 text-gray-900 drop-shadow-sm"}>
                <div className={"flex flex-row items-center"}>
                    <ArrowPathIcon className={clsx("w-5 h-5 mr-1 text-gray-600/70 drop-shadow-xl")} />
                    <h4 className={"font-medium text-gray-700"}>{t("Rebooking")}</h4>
                </div>
                <ModalScrollableClose />
            </div>

            <div
                className={"flex w-full flex-col px-3 pt-3 pb-4 bg-gray-50 overflow-hidden h-auto rounded-bl-lg rounded-br-lg"}>
                <div className={"flex overflow-y-auto h-full w-full"}>
                    <form className={"w-full gap-3 flex flex-col"}
                          onSubmit={handleSubmit(onSubmitHandler, onErrorHandler)}>
                        <CardComponent className={"grid grid-cols-2 gap-3"}>
                            <input type={"hidden"} {...register("reservation_id")} />
                            <BookingInputTimePicker
                                className={"col-span-1"}
                                errors={errors}
                                register={register}
                                setValue={setValue}
                                name={"date"}
                                label={t("Date")}
                                required={true}
                                onlyUpcomingDates={true}
                            />
                            <BookingInputTimePicker
                                className={"col-span-1"}
                                errors={errors}
                                register={register}
                                setValue={setValueTimeHandler}
                                name={"time"}
                                label={t("Time")}
                                required={true}
                                onlyTime={true}
                            />
                            {/*<div className={"col-span-2 text-center mx-auto"}>*/}
                            {/*    <UiButton*/}
                            {/*        icon={<CheckIcon className={"w-4 h-4 flex-none"} />}*/}
                            {/*        title={t("Rebook")}*/}
                            {/*        onClick={handleSubmit(onSubmitHandler, onErrorHandler)}*/}
                            {/*        style={UiButtonStyle.Add}*/}
                            {/*        size={UiButtonSize.Normal}*/}
                            {/*    />*/}
                            {/*</div>*/}
                        </CardComponent>
                        <UiButton
                            className={"w-full"}
                            icon={<CheckIcon className={"w-4 h-4 flex-none"} />}
                            title={t("Rebook")}
                            onClick={handleSubmit(onSubmitHandler, onErrorHandler)}
                            style={UiButtonStyle.Add}
                            size={UiButtonSize.Medium}
                        />
                    </form>
                </div>
            </div>
        </CardComponent>
    );
};

export default BookingSellerRebookingModal;